/**
 * Factor-type → MessageDescriptor selection for the step-up auth phases.
 *
 * Copy variants are taken verbatim from
 * ss-ui-client/src/global/step-up-authentication/helpers.jsx (getDisplayForFactor)
 * and .../phases/challenge.jsx (EnrollmentTypeMessageMap / getMessages).
 */

import { AUTH0_MFA_TYPES } from './mfa-constants';
import { getIsAuthenticator, getIsEmail, getIsSms, getIsVoice } from './mfa-helpers';
import { mfaMessages } from './mfa-messages';

import type { MfaEnrollment } from './mfa-types';
import type { MessageDescriptor } from 'react-intl';

export type MfaChallengeCopy = {
  titleMessage: MessageDescriptor;
  subheaderMessage: MessageDescriptor;
};

const ENROLLMENT_TYPE_MESSAGE_MAP: Record<string, MfaChallengeCopy> = {
  [AUTH0_MFA_TYPES.email]: {
    titleMessage: mfaMessages.challengeEmailTitle,
    subheaderMessage: mfaMessages.challengeEmailSubheader,
  },
  [AUTH0_MFA_TYPES.sms]: {
    titleMessage: mfaMessages.challengePhoneTitle,
    subheaderMessage: mfaMessages.challengeSmsSubheader,
  },
  [AUTH0_MFA_TYPES.voice]: {
    titleMessage: mfaMessages.challengePhoneTitle,
    subheaderMessage: mfaMessages.challengeVoiceSubheader,
  },
  [AUTH0_MFA_TYPES.totp]: {
    titleMessage: mfaMessages.challengeAuthenticatorTitle,
    subheaderMessage: mfaMessages.challengeAuthenticatorSubheader,
  },
};

/** Title + subheader copy for the challenge phase, chosen by enrollment factor type. */
export const getChallengeCopy = (enrollment: MfaEnrollment): MfaChallengeCopy => {
  if (getIsAuthenticator(enrollment)) {
    return ENROLLMENT_TYPE_MESSAGE_MAP[AUTH0_MFA_TYPES.totp];
  }

  if (getIsEmail(enrollment)) {
    return ENROLLMENT_TYPE_MESSAGE_MAP[AUTH0_MFA_TYPES.email];
  }

  if (getIsSms(enrollment)) {
    return ENROLLMENT_TYPE_MESSAGE_MAP[AUTH0_MFA_TYPES.sms];
  }

  if (getIsVoice(enrollment)) {
    return ENROLLMENT_TYPE_MESSAGE_MAP[AUTH0_MFA_TYPES.voice];
  }

  return ENROLLMENT_TYPE_MESSAGE_MAP[AUTH0_MFA_TYPES.email];
};

/**
 * Descriptor for the enrollments table identifying the enrollment type.
 * Voice & SMS are separate enrollments but listed once since both use same Phone number.
 */
export const getEnrollmentListItemMessage = (enrollment: MfaEnrollment): MessageDescriptor => {
  if (getIsEmail(enrollment)) {
    return mfaMessages.emailEnrollmentListItem;
  }

  if (getIsAuthenticator(enrollment)) {
    return mfaMessages.totpEnrollmentListItem;
  }

  return mfaMessages.smsOrVoiceEnrollmentListItem;
};

/**
 * Descriptor for an enrollment's radio label, or `null` when the label is plain
 * text (email addresses and unrecognised factor types are rendered verbatim).
 */
export const getEnrollmentFactorMessage = (enrollment: MfaEnrollment): MessageDescriptor | null => {
  if (getIsEmail(enrollment)) {
    return mfaMessages.emailEnrollment;
  }

  if (getIsSms(enrollment)) {
    return mfaMessages.smsEnrollment;
  }

  if (getIsVoice(enrollment)) {
    return mfaMessages.voiceEnrollment;
  }

  if (getIsAuthenticator(enrollment)) {
    return mfaMessages.totpEnrollment;
  }

  return null;
};

/** Plain-text label used when `getEnrollmentFactorMessage` returns `null`. */
export const getEnrollmentFactorFallbackText = (enrollment: MfaEnrollment): string => {
  return getIsEmail(enrollment) ? enrollment.name : enrollment.type;
};
